/**
 * Pending-command timeout tracker for the realtime control channel.
 *
 * The console sends a device command, gets a gateway ack (delivered or not),
 * and later a command-result from the player. A zombie player (frozen tab,
 * half-dead socket) can swallow a delivered command without ever answering,
 * which used to look like success. This map arms one timer per commandId at
 * send time and fires `onTimeout` if neither a delivered:false ack nor a
 * command-result settles it in time, so the operator gets a "no response"
 * toast instead of silence.
 *
 * Pure and timer-injectable so it unit-tests without a DOM or fake clocks.
 */

type TimerHandle = ReturnType<typeof setTimeout> | number;

export interface CommandTimeoutTracker {
  /** Arm the timeout for a just-sent command. Re-arming an id resets it. */
  start: (commandId: string) => void;
  /**
   * Settle a command (ack delivered:false or command-result arrived).
   * Returns true when a pending timer was cleared, false for unknown ids —
   * results for commands sent by other sessions are ignored.
   */
  settle: (commandId: string) => boolean;
  /** Cancel every pending timer (provider unmount / socket teardown). */
  clear: () => void;
  /** Number of commands still awaiting a response. */
  pendingCount: () => number;
}

export function createCommandTimeoutTracker(options: {
  timeoutMs: number;
  onTimeout: (commandId: string) => void;
  /** Injectable timers for tests; default to the global ones. */
  setTimer?: (fn: () => void, ms: number) => TimerHandle;
  clearTimer?: (handle: TimerHandle) => void;
}): CommandTimeoutTracker {
  const { timeoutMs, onTimeout } = options;
  const setTimer =
    options.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const clearTimer =
    options.clearTimer ?? ((handle: TimerHandle) => clearTimeout(handle as ReturnType<typeof setTimeout>));

  const pending = new Map<string, TimerHandle>();

  const settle = (commandId: string): boolean => {
    const handle = pending.get(commandId);
    if (handle === undefined) return false;
    pending.delete(commandId);
    clearTimer(handle);
    return true;
  };

  return {
    start: (commandId: string) => {
      // A duplicate id resets the window rather than leaking the old timer.
      settle(commandId);
      const handle = setTimer(() => {
        pending.delete(commandId);
        onTimeout(commandId);
      }, timeoutMs);
      pending.set(commandId, handle);
    },
    settle,
    clear: () => {
      for (const handle of pending.values()) clearTimer(handle);
      pending.clear();
    },
    pendingCount: () => pending.size,
  };
}
